import {useQuery} from "@apollo/client";
import omit from "lodash/omit";
import {Suspense} from "react";
import {Helmet} from "react-helmet-async";
import {Navigate, useLocation, useParams, useSearchParams} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import ContentContainer from "@/components/layout/ContentContainer.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {ErrorFallback} from "@/lib/handlers/ErrorFallback.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {FindCheckResponse, GQL_EDIT_CHECK_DATA} from "@/lib/queries/checks.ts";
import {QUERY} from "@/pages/checks/constants.ts";
import {CreateEditCheck} from "@/pages/checks/update/CreateEditCheck.tsx";

export const EditCheckPage = () => {
  const [searchParams] = useSearchParams();
  const {user} = useCurrentUserContext();
  const {id} = useParams();
  const location = useLocation();
  const refetchQueries = location.state?.refetchQueries || [];
  const {data, loading} = useQuery<FindCheckResponse>(GQL_EDIT_CHECK_DATA, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    variables: {id: Number(id)},
  });
  const service = data?.findCheck;

  if (!user?.has_write_access) {
    return <Navigate to="/access-denied" />;
  }

  if (loading || !service) {
    return <LoadingContainer />;
  }

  const duplicate = searchParams.get(QUERY.DUPLICATE) === "true";
  const title = `${duplicate ? "Duplicate - " : ""} ${service.name}`;

  return (
    <UpErrorBoundary
      fallback={
        <ContentContainer title={title}>
          <ErrorFallback />
        </ContentContainer>
      }
    >
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Suspense fallback={<LoadingContainer />}>
        <CreateEditCheck
          service={duplicate ? omit(service, ["id", "uuid", "selected_third_party_service_component"]) : service}
          refetchQueries={refetchQueries}
          title={title}
        />
      </Suspense>
    </UpErrorBoundary>
  );
};
